/**
 * Endpoint key utilities — pure, dependency-free.
 *
 * Convention shared with the backend: each liveness Probe.endpointKey is
 * stored in the form `<METHOD> <path>` (e.g. `GET /api/v3/masterchainInfo`,
 * `WS /api/streaming/v2/ws`, `POST /api/v2/jsonRPC#runGetMethod`). The
 * backend writes it; the frontend parses it into structured form for display.
 *
 * Legacy data (probes recorded before the convention) has no method prefix —
 * we surface those as method=null and path=<raw key>.
 */

export type EndpointMethod = 'GET' | 'POST' | 'WS' | 'SSE';
export type Network = 'mainnet' | 'testnet';

export interface ParsedEndpointKey {
  method: EndpointMethod | null;
  path: string;
}

const KNOWN_METHODS: ReadonlySet<EndpointMethod> = new Set([
  'GET',
  'POST',
  'WS',
  'SSE',
]);

export function parseEndpointKey(key: string): ParsedEndpointKey {
  if (!key) return { method: null, path: '' };
  const spaceIdx = key.indexOf(' ');
  if (spaceIdx === -1) return { method: null, path: key };
  const head = key.slice(0, spaceIdx);
  const tail = key.slice(spaceIdx + 1);
  if (KNOWN_METHODS.has(head as EndpointMethod)) {
    return { method: head as EndpointMethod, path: tail };
  }
  return { method: null, path: key };
}

const HOSTS: Record<Network, string> = {
  mainnet: 'toncenter.com',
  testnet: 'testnet.toncenter.com',
};

export function formatEndpointUrl(args: {
  method: EndpointMethod | null;
  path: string;
  network: Network;
}): string {
  if (args.method === null) return '';
  // Synthetic probes (txlifecycle inspectors like
  // `POST /lifecycle/_cycle-status`) borrow the `<METHOD> <path>` title
  // shape for storage but do NOT hit any URL — they're pure functions over
  // the captured Cycle state. Rendering them as `https://toncenter.com/...`
  // misleads users into thinking it's a real endpoint they can hit.
  if (isSyntheticPath(args.path)) return '';
  const host = HOSTS[args.network];
  const scheme = args.method === 'WS' ? 'wss' : 'https';
  const pathWithoutFragment = args.path.split('#')[0];
  const cleanPath = stripFreshnessSuffix(pathWithoutFragment);
  return `${scheme}://${host}${cleanPath}`;
}

/**
 * Synthetic probes share the `<METHOD> <path>` title shape (so they slot
 * into the same Probe table and rendering pipeline as real endpoints) but
 * do not correspond to any HTTP endpoint. Convention: paths under
 * `/lifecycle/_*` belong to txlifecycle inspectors — see
 * `the autotests suites/probes/txlifecycle/probes.go`.
 */
export function isSyntheticPath(path: string): boolean {
  return path.startsWith('/lifecycle/_');
}

/**
 * `true` iff this endpoint-key is a synthetic inspector. Use to suppress
 * the method badge and URL line in the UI — synthetics aren't network
 * calls, so showing `POST` / `https://...` is a lie.
 */
export function isSyntheticEndpoint(endpointKey: string): boolean {
  const { path } = parseEndpointKey(endpointKey);
  return isSyntheticPath(path);
}

const FRESHNESS_SUFFIX = ' (freshness)';

/**
 * `true` iff the endpoint-key ends with the literal `(freshness)` suffix
 * convention (e.g. `'GET /api/v3/actions (freshness)'`). Mirrors the
 * server-side validator in `liveness/validate.ts`.
 */
export function isFreshnessEndpoint(endpointKey: string): boolean {
  return endpointKey.endsWith(FRESHNESS_SUFFIX);
}

/**
 * Drop the ` (freshness)` suffix from a path or endpoint-key, if present.
 * Used to render freshness probes' display path without the marker.
 */
export function stripFreshnessSuffix(value: string): string {
  return value.endsWith(FRESHNESS_SUFFIX)
    ? value.slice(0, -FRESHNESS_SUFFIX.length)
    : value;
}

/**
 * The `?lagEndpoint=` deep-link value for ANY endpoint key — the display
 * path without method or freshness marker (`'GET /api/v3/actions (freshness)'`
 * → `'/api/v3/actions'`). One shared definition so the URL the SPA writes,
 * the deep-link matcher, and the OG preview backend all agree.
 */
export function endpointLinkPath(endpointKey: string): string {
  return stripFreshnessSuffix(parseEndpointKey(endpointKey).path);
}
